import { useState } from "react";
import { Lock, CalendarClock, BadgeCheck, Globe } from "lucide-react";
import { useLang } from "../context/LangContext";
import { WHATSAPP_DISPLAY, TELEGRAM_DISPLAY, waLink, TELEGRAM_URL, OFFICIAL_URL } from "../constants";
import { WhatsAppIcon, TelegramIcon } from "./icons";
import { Reveal } from "./Reveal";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-300 sm:text-sm ${
      active
        ? "border-blue-400/60 bg-blue-500/20 text-blue-200 shadow-[0_0_18px_rgba(59,130,246,0.35)]"
        : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25 hover:text-slate-200"
    }`}
  >
    {children}
  </button>
);

export const ContactSection = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const [spend, setSpend] = useState(null);
  const [usage, setUsage] = useState(null);

  const buildMsg = () => {
    if (!spend && !usage) return c.msgDefault;
    let m = `${c.msgPrefix}${spend || "-"}`;
    if (usage) m += `${c.msgUsage}${usage}`;
    return `${m}${c.msgSuffix}`;
  };

  return (
    <section id="contact" className="relative py-20 lg:py-28">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[600px] -translate-x-1/2 rounded-full bg-blue-600/10 blur-[130px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-start gap-8 lg:grid-cols-[1.25fr_0.75fr]">
          {/* left: selector + contact buttons */}
          <Reveal>
            <div className="glass rounded-[28px] p-8 sm:p-10">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400">{c.tag}</span>
              <h2 className="mt-4 font-display text-3xl font-black text-white sm:text-4xl">{c.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.subtitle}</p>

              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{c.spendLabel}</p>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {c.spendOptions.map((o, i) => (
                    <Chip
                      key={o}
                      testid={`spend-option-${i}`}
                      active={spend === o}
                      onClick={() => setSpend(spend === o ? null : o)}
                    >
                      {o}
                    </Chip>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{c.usageLabel}</p>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {c.usageOptions.map((o, i) => (
                    <Chip
                      key={o}
                      testid={`usage-option-${i}`}
                      active={usage === o}
                      onClick={() => setUsage(usage === o ? null : o)}
                    >
                      {o}
                    </Chip>
                  ))}
                </div>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                <a
                  href={waLink(buildMsg())}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="group flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-500 px-6 py-5 text-white shadow-[0_0_36px_rgba(34,197,94,0.35)] transition-transform duration-300 hover:scale-[1.03]"
                >
                  <WhatsAppIcon className="h-6 w-6" />
                  <span className="text-left">
                    <span className="block text-sm font-black">{c.whatsappBtn}</span>
                    <span className="block text-[11px] font-medium opacity-80">{WHATSAPP_DISPLAY}</span>
                  </span>
                </a>
                <a
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-telegram-btn"
                  className="group flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-500 px-6 py-5 text-white shadow-[0_0_36px_rgba(56,160,240,0.35)] transition-transform duration-300 hover:scale-[1.03]"
                >
                  <TelegramIcon className="h-6 w-6" />
                  <span className="text-left">
                    <span className="block text-sm font-black">{c.telegramBtn}</span>
                    <span className="block text-[11px] font-medium opacity-80">{TELEGRAM_DISPLAY}</span>
                  </span>
                </a>
              </div>

              {/* secondary brand trust link — must not compete with main CTAs */}
              <a
                href={OFFICIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-official-link"
                className="mt-6 flex items-center justify-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-blue-300"
              >
                <Globe className="h-3.5 w-3.5" />
                {lang === "cn" ? "了解更多，请访问 MB PAY VIA 官方网站" : "Learn more on the MB PAY VIA Official Website"}
              </a>
            </div>
          </Reveal>

          {/* right: offer card */}
          <Reveal delay={0.12}>
            <div
              data-testid="contact-offer-card"
              className="glow-card relative flex h-full flex-col justify-center overflow-hidden rounded-[28px] bg-gradient-to-b from-blue-950/70 via-navy-900/90 to-navy-950 p-8 text-center sm:p-10"
            >
              <div className="animate-pulse-glow pointer-events-none absolute -top-20 left-1/2 h-52 w-72 -translate-x-1/2 rounded-full bg-blue-500/25 blur-[80px]" />
              <div className="relative">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-400">{c.offerFrom}</p>
                <p className="text-gradient mt-3 font-display text-7xl font-black tracking-tight sm:text-8xl">0.9%</p>

                <div className="mx-auto my-8 h-px w-2/3 bg-gradient-to-r from-transparent via-blue-400/40 to-transparent" />

                <div className="space-y-5">
                  <div className="flex items-center justify-center gap-3">
                    <CalendarClock className="h-5 w-5 text-blue-400" />
                    <div className="text-left">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{c.offerJoin}</p>
                      <p className="font-display text-xl font-black text-white">{c.offerJoinDate}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <BadgeCheck className="h-5 w-5 text-blue-400" />
                    <div className="text-left">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{c.offerApproved}</p>
                      <p className="font-display text-xl font-black text-white">{c.offerLock}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-9 flex items-center justify-center gap-2 rounded-2xl border border-amber-400/25 bg-amber-400/[0.07] px-5 py-3.5">
                  <Lock className="h-4 w-4 text-amber-300" />
                  <span className="text-xs font-bold tracking-wide text-amber-200">{t.hero.lockLine}</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
